import React, { useEffect, useRef } from 'react';

import HCaptcha from '@hcaptcha/react-hcaptcha';
import { makeStyles, Theme } from '@material-ui/core';
import { useForm } from 'react-hook-form';
import { hashHistory } from 'react-router';

import { analyticsService, flagService } from '../../../core/services';
import { utils } from '../../../utils';
import { Track } from '../../../weborder/analytics';
import * as Api from '../../../weborder/Api';
import { DispatchProps } from '../../../weborder/component/PhoneNumber';
import * as Store from '../../../weborder/Store';
import { Button, Checkbox, TextField, Typography } from '../../atoms';
import { Dialog } from '../../layouts';

const useStyles = makeStyles((theme: Theme) => ({
  loginDialog: {
    '& .fd-text__h1': {
      marginBottom: theme.spacing(0.5),
    },

    '& .fd-text__body1': {
      color: theme.palette.grey[800],
      marginBottom: theme.spacing(2),

      [theme.breakpoints.up('sm')]: {
        marginBottom: theme.spacing(2.5),
      },
    },

    '& .fd-text__caption span': {
      color: theme.palette.grey[800],

      '& a': {
        color: theme.palette.text.primary,
      },
    },
  },
}));

interface FormInput {
  phoneInput: string;
}

type OuterProps = {
  configData: Api.AppConfig;
  enableRememberMe: boolean;
  hasPhoneNumberError?: boolean;
  languageCode: Store.Language['code'];
  lastRestaurantUrl: string;
  loginStartPoint: string;
  params: any;
  phoneNumberConfig: Api.PhoneNumberConfig;
  rememberMe: boolean;
  translate: any;
};

type Props = OuterProps & DispatchProps;

export function LoginDialog(props: Props): JSX.Element {
  const classes = useStyles();
  const {
    actions,
    configData,
    enableRememberMe,
    hasPhoneNumberError,
    languageCode,
    lastRestaurantUrl,
    loginStartPoint,
    params,
    phoneNumberConfig,
    rememberMe,
    translate,
  } = props;

  const { register, handleSubmit, setValue, formState } = useForm<FormInput>({
    mode: 'onChange',
  });
  const { isSubmitting } = formState;

  const hCaptchaRef = useRef<HCaptcha>(null);
  const isHCaptchaEnabled =
    process.env.REACT_APP_HCAPTCHA_SITE_KEY &&
    flagService.isFlagOn('phoneSmsHCaptcha');

  useEffect(() => {
    const [loginStartPointPage] = loginStartPoint.substring(1).split('/');
    const loginStartPointString = loginStartPointPage || 'n/a';

    const trackStartLogin = (): void => {
      const eventData = {
        amp_label: 'Start Login',
        amp_view: loginStartPointString,
      };
      analyticsService.track(eventData);
    };

    trackStartLogin();
  }, []);

  const getCodeUrl = (phoneNumber: string): string => {
    let base = `/code/${phoneNumber}`;

    if (params.dlid) {
      base += `/${params.dlid}`;
    }

    return base;
  };

  const isValidPhoneNumber = (textFieldValue): boolean => {
    const phoneNumber = textFieldValue.replace(/\s/g, '');
    const hasPlusPrefix =
      phoneNumber && phoneNumber.charAt(0) && phoneNumber.charAt(0) === '+';
    const newPhoneNumber =
      (hasPlusPrefix ? '+' : '') + phoneNumber.replace(/\D/g, '');

    const isNumber = newPhoneNumber.match(/^\+?\d*$/);
    setValue('phoneInput', newPhoneNumber);

    if (!isNumber) {
      return false;
    }

    const { AutocompletePhoneNumberLength, Prefix } = phoneNumberConfig || {
      AutocompletePhoneNumberLength: 12,
      Prefix: '+1',
    };

    if (
      phoneNumber.length === AutocompletePhoneNumberLength &&
      phoneNumber.startsWith(Prefix) &&
      !hasPlusPrefix
    ) {
      onSubmit({ phoneInput: textFieldValue });
    }
    return true;
  };

  const toggleRememberMe = () => actions.setRememberMe(!rememberMe);

  const onHCaptchaVerify = (token: string): void => {
    actions.setHCaptchaToken({
      requestPhoneLoginCodeSmsToken: token,
    });
  };

  const onSubmit = async (data: FormInput): Promise<void> => {
    if (isHCaptchaEnabled) {
      await hCaptchaRef.current.execute({ async: true });
    }

    Track('Login', 'Submit Phone Number Button');
    hashHistory.push(getCodeUrl(data.phoneInput));
  };

  const renderTermsOfServiceLink = (link): JSX.Element => (
    <a
      href={link.linkUrl}
      key={link.linkUrl}
      rel="noopener noreferrer"
      target="_blank"
    >
      {translate(link.key)}
    </a>
  );

  const renderServiceTerms = (): string[] => {
    const content = translate('Login_LoginDialog_ServiceTerms').split('%@');

    const urlLanguageCode = languageCode ? `${languageCode}/` : '';
    const linkUrl = `https://www.flipdish.com/${urlLanguageCode}flipdish-consumer-terms-and-conditions/`;
    const tncLinks = [
      {
        key: 'Terms_and_conditions',
        linkUrl: configData.TermsAndConditionsUrl,
      },
      {
        key: 'Terms_and_conditions',
        linkUrl,
      },
      {
        key: 'Privacy_policy',
        linkUrl: configData.PrivacyPolicyUrl,
      },
    ];

    const serviceTerms = content.reduce((acc, sentence, index) => {
      acc.push(
        <span key={utils.make.keyFromText(sentence, 'term')}>{sentence}</span>,
      );

      if (index < tncLinks.length) {
        const linkKey = utils.make.keyFromText(
          tncLinks[index].key,
          `term-link-${index}`,
        );

        acc.push(
          <span key={linkKey}>
            {renderTermsOfServiceLink(tncLinks[index])}
          </span>,
        );
      }

      return acc;
    }, []);

    return serviceTerms;
  };

  const phoneInputAriaLabel = `${translate(
    'Enter_your_mobile_number',
  )} ${translate('Mobile_verification')}`;

  const defaultPhoneNumber = phoneNumberConfig
    ? phoneNumberConfig.Prefix
    : '+1';

  const dialogActions = (): JSX.Element => (
    <Button
      data-fd="phone-submit"
      fullWidth
      key="submit-button"
      onClick={handleSubmit(onSubmit)}
      status={formStatus}
    >
      {translate('Login_LoginDialog_Continue')}
    </Button>
  );

  const getRememberMeText = (): string => {
    if (enableRememberMe) {
      return translate('Login_LoginDialog_RememberMeFunctionalCookies');
    }

    return '';
  };

  const handleOnBackLink = (): void => {
    /* if on checkout page, back button should go back to last menu page,
    otherwise back will navigate to checkout page again causing login to appear again */
    const backurl = loginStartPoint.includes('/checkout')
      ? lastRestaurantUrl
      : loginStartPoint;
    hashHistory.push(backurl);
  };

  const numberErrorText = hasPhoneNumberError
    ? translate('Login_LoginDialog_helperTestErrorMessage')
    : '';

  const formStatus = isSubmitting ? 'loading' : 'default';
  const fieldStatus = hasPhoneNumberError ? 'error' : 'default';

  return (
    <Dialog
      actions={dialogActions()}
      className={classes.loginDialog}
      dialogName="login"
      onTitleClick={handleOnBackLink}
      open
    >
      <Typography variant="h1">
        {translate('Login_LoginDialog_MobileNumber')}
      </Typography>
      <Typography>{translate('Login_LoginDialog_SmsMessage')}</Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        {isHCaptchaEnabled && (
          <HCaptcha
            onVerify={onHCaptchaVerify}
            ref={hCaptchaRef}
            sitekey={process.env.REACT_APP_HCAPTCHA_SITE_KEY}
            size="invisible"
          />
        )}

        <TextField
          aria-label={phoneInputAriaLabel}
          autoFocus
          data-fd="phone-input"
          defaultValue={defaultPhoneNumber}
          helperText={numberErrorText}
          id="phoneInput"
          inputRef={register({
            required: false,
            validate: isValidPhoneNumber,
          })}
          status={fieldStatus}
          key="phoneInput"
          name="phoneInput"
          placeholder="e.g. +44775555555"
          type="tel"
        />

        {enableRememberMe && (
          <Checkbox
            checked={rememberMe}
            data-fd="rememberMe"
            id="remember_me"
            label={translate('remember_me')}
            onChange={toggleRememberMe}
          />
        )}
      </form>

      <Typography variant="caption">
        {renderServiceTerms()} {getRememberMeText()}
      </Typography>
    </Dialog>
  );
}
